import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ModalProps } from "./types";
import CloseIcon from "../../assets/svg/close-icon";

const Modal = ({
  open,
  onOpenChange,
  children,
  title,
  description,
  showCloseButton = true,
  preventOutsideClick = false,
  className = "",
  overlayClassName = "",
  contentClassName = "",
  contentStyle = {},
  headerClassName = "",
  titleClassName = "",
  closeButtonClassName = "",
  bodyClassName = "",
  isFullScreen = false,
  showHeader = true,
  closeOnEscape = true,
  customCloseButton,
  customHeader,
  disableDefaultAnimation = false,
  zLevel = 1,
}: ModalProps) => {
  const overlayZIndex = zLevel * 1000;
  const contentZIndex = zLevel * 1000 + 1;
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (preventOutsideClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onOpenChange(false);
  };

  const handlePointerDownOutside = (event: {
    preventDefault: () => void;
    target?: unknown;
  }) => {
    const target = event?.target as HTMLElement | null;
    if (target?.closest("[data-modal-ignore-outside]")) {
      event.preventDefault();
      return;
    }

    if (preventOutsideClick) {
      event.preventDefault();
      return;
    }
    onOpenChange(false);
  };

  const handleInteractOutside = (event: {
    preventDefault: () => void;
    target?: unknown;
  }) => {
    const target = event?.target as HTMLElement | null;
    if (target?.closest("[data-modal-ignore-outside]")) {
      event.preventDefault();
      return;
    }

    if (preventOutsideClick) {
      event.preventDefault();
      return;
    }
    onOpenChange(false);
  };

  const handleEscapeKey = (event: KeyboardEvent) => {
    if (!closeOnEscape) {
      event.preventDefault();
    }
  };

  const renderCloseButton = () => {
    if (!showCloseButton) return null;

    if (customCloseButton) {
      return customCloseButton;
    }

    return (
      <Dialog.Close asChild>
        <button
          className={`shrink-0 w-[15px] h-[15px] bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110 ${closeButtonClassName}`}
          aria-label="Close modal"
        >
          <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
        </button>
      </Dialog.Close>
    );
  };

  const renderHeader = () => {
    if (!showHeader) return null;

    if (customHeader) {
      return (
        <div
          className={`px-6 py-4 flex items-center justify-between ${headerClassName}`}
        >
          {customHeader}
          {showCloseButton && renderCloseButton()}
        </div>
      );
    }

    return (
      <div className={`flex items-center justify-between ${headerClassName}`}>
        <Dialog.DialogTitle asChild className="text-left">
          {title ? (
            <h1
              className={`text-lg font-semibold text-gray-900 ${titleClassName}`}
            >
              {title}
            </h1>
          ) : (
            <VisuallyHidden>Dialog</VisuallyHidden>
          )}
        </Dialog.DialogTitle>
        <Dialog.Description asChild>
          <VisuallyHidden>{description || ""}</VisuallyHidden>
        </Dialog.Description>
        {renderCloseButton()}
      </div>
    );
  };

  const finalContentClassName = `fixed rounded-lg shadow-lg min-w-[300px] max-w-[90vw] max-h-[90vh] overflow-auto transition-all duration-300 ease-out transform ${contentClassName} ${
    isFullScreen ? "inset-0 w-full h-full overflow-auto" : ""
  } ${className}`;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={`fixed inset-0 bg-black/80 transition-opacity duration-300 ease-out ${overlayClassName}`}
          style={{ zIndex: overlayZIndex }}
          onClick={handleOverlayClick}
        />
        <Dialog.Content
          className={`${finalContentClassName} ${
            !disableDefaultAnimation
              ? open
                ? "animate-modal-enter"
                : "animate-modal-exit"
              : ""
          }`}
          onPointerDownOutside={handlePointerDownOutside}
          onEscapeKeyDown={handleEscapeKey}
          onInteractOutside={handleInteractOutside}
          style={{
            zIndex: contentZIndex,
            transform: !disableDefaultAnimation
              ? "translate(-50%, -50%)"
              : undefined,
            left: "50%",
            top: "50%",
            ...contentStyle,
          }}
        >
          {showHeader ? (
            renderHeader()
          ) : (
            <>
              <Dialog.DialogTitle asChild>
                <VisuallyHidden>{title || "Dialog"}</VisuallyHidden>
              </Dialog.DialogTitle>
              <Dialog.Description asChild>
                <VisuallyHidden>{description || ""}</VisuallyHidden>
              </Dialog.Description>
            </>
          )}
          <div className={`p-6 ${bodyClassName}`}>{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Modal;
